import React, { useState, useEffect, useRef } from "react";

import {
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert, 
  AlertTitle,
  List, 
  ListItem
} from "@mui/material";

import { getJobOrder, putJobOrder } from "../API/jobOrderAPI";
import { getCacheClients, getClient, putClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import { postJobOrderLogs } from "../API/jobOrderLogsAPI";
import { validateJobOrder } from './utils/JobOrderValidation';

import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import WorkIcon from "@mui/icons-material/Work";
import BadgeIcon from "@mui/icons-material/Badge";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { 
  JOB_TYPES,
  JOB_DESCRIPTIONS,
  DOCUMENTS_TO_BRING,
  EQUIPMENT
} from "./utils/JobOrderConstants";

const useFetchTechnicians = () => {
  const [technicians, setTechnicians] = useState(null); // State to store fetched data

  useEffect(() => {
    getTechnicians()
      .then((data) => {
        const sortedTechnicians = data.sort((a, b) =>
          a.lastname.localeCompare(b.lastname)
        );
        setTechnicians(sortedTechnicians); // Set the state with sorted data
      })
      .catch((error) => console.error("Error fetching technicians:", error));
  }, []);

  return technicians;
};


function EditJobOrder() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [uiState, setUIState] = useState({
    isFetchingClient: false,
  });

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [clientsCache, setClientsCache] = useState([]);
  const [client, setClient] = useState(null);
  const [selectedTechIds, setSelectedTechIds] = useState([]);
  const technicians = useFetchTechnicians();

  const [jobOrder, setJobOrder] = useState({
    client_id: "",
    client: { id: "", name: "", address: {}, contact: {} },
    job_types: [],
    job_description: [],
    special_notes: "",
    job_date: "",
    start_time: "",
    end_time: "",
    technicians: [],
    status: "Not Done",
    service_report_status: "Not Done",
    billing_record_status: "Not Done",
    endorser_name: "",
    endorser_contact_number: "",
    current_status: "",
    documents_to_bring: [],
    payment_method: "",
    schedule_confirmed: "",
    client_complaints: "",
    equipment: [],
    form_type: "",
    appointment_type: "",
    treatment_type: "",
  });

  const [modalOpen, setModalOpen] = useState(false);

  // Opens the modal
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  // Closes the modal
  const handleDialogClose = () => {
    setModalOpen(false);
  };

  const handleCloseAssignment = () => {
    setModalOpen(false);
  };


  useEffect(() => {
    const fetchClientsCache = async () => {
      setIsLoading(true);
      try {
        const fetchedClients = await getCacheClients();
        setClientsCache(fetchedClients);
      } catch (err) {
        console.error("Error fetching clients cache:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchClientsCache();
  }, []);

  useEffect(() => {
    const fetchAndSetupJobOrder = async () => {
      setIsLoading(true);
      try {
        // Fetch job order by ID
        const data = await getJobOrder(id);
        const fetchedClient = data.client_id
          ? await getClient(data.client_id)
          : null;

        // Update client and job order states
        setClient(fetchedClient);
        setJobOrder({
          client_id: data.client_id || "",
          client: {
            id: fetchedClient?.id || "",
            name: fetchedClient?.name || "",
            address:
              fetchedClient?.addresses?.length === 1
                ? fetchedClient.addresses[0]
                : data.client?.address || {},
            contact:
              fetchedClient?.contacts?.length === 1
                ? {
                  ...fetchedClient.contacts[0],
                  number:
                    fetchedClient.contacts[0]?.numbers?.length === 1
                      ? fetchedClient.contacts[0].numbers[0]
                      : "",
                  email:
                    fetchedClient.contacts[0]?.emails?.length === 1
                      ? fetchedClient.contacts[0].emails[0]
                      : "",
                }
                : data.client?.contact || {},
          },
          job_types: data.job_types || [],
          job_description: data.job_description || [],
          special_notes: data.special_notes || "",
          job_date: data.job_date || "",
          start_time: data.start_time || "",
          end_time: data.end_time || "",
          technicians:
            data.technicians?.map((tech) => ({
              firstname: tech.firstname || "",
              lastname: tech.lastname || "",
              id: tech.id || "",
            })) || [],
          status: data.status || "Not Done",
          service_report_status: data.service_report_status || "Not Done",
          billing_record_status: data.billing_record_status || "Not Done",
          endorser_name: data.endorser_name || "",
          endorser_contact_number: data.endorser_contact_number || "",
          current_status: data.current_status || "",
          documents_to_bring: data.documents_to_bring || [],
          payment_method: data.payment_method || "",
          schedule_confirmed: data.schedule_confirmed || "",
          client_complaints: data.client_complaints || "",
          equipment: data.equipment || [],
          form_type: data.form_type || "",
          appointment_type: data.appointment_type || "",
          treatment_type: data.treatment_type || "",
        });

        setSelectedTechIds(data.technicians?.map((tech) => (tech.id)) || [])

        // Mark initial setup as complete
      } catch (err) {
        setError("Failed to fetch job order");
      } finally {
        setIsLoading(false);
      }
    };

    // Ensure technicians are loaded before fetching job order
    if (technicians?.length) {
      fetchAndSetupJobOrder();
    }
  }, [id, clientsCache, technicians]);


  const handleAddressChange = (selectedAddress) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        address: selectedAddress, // Update the address field with the new object
      },
    }));
  };

  const handleContactChange = (selectedContact) => {
    const updatedContact = selectedContact
      ? {
        ...selectedContact,
        number:
          selectedContact.numbers?.length === 1
            ? selectedContact.numbers[0]
            : "",
        email:
          selectedContact.emails?.length === 1
            ? selectedContact.emails[0]
            : "",
      }
      : {};

    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        contact: updatedContact, // Update the contact field with the new object
      },
    }));
  };

  const handleContactNumberChange = (selectedNumber) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        contact: {
          ...prev.client.contact, // Preserve other properties in contact
          number: selectedNumber, // Update the number field with the new object
        },
      },
    }));
  };

  const handleContactEmailChange = (selectedEmail) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      client: {
        ...prev.client, // Preserve other properties in client
        contact: {
          ...prev.client.contact, // Preserve other properties in contact
          email: selectedEmail, // Update the email field with the selected object
        },
      },
    }));
  };

  const handleJobOrderSpecialNotesChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handleJobOrderScheduleChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  // const handleSearchClient = (event, value) => {
  //   setJobOrder((prevJobOrder) => ({
  //     ...prevJobOrder, // Preserve other properties in jobOrder
  //     client_id: value ? value.id : "", // Update client_id based on value
  //     client: value
  //       ? {
  //         ...prevJobOrder.client, // Preserve other client properties if needed
  //         id: value.id,
  //         name: value.name,
  //       }
  //       : {
  //         id: "",
  //         name: "",
  //         address: {},
  //         contact: {}, // Corrected to `contact` as per your new structure
  //       },
  //   }));

  //   if (value === null) {
  //     // Set client to null only when value is null
  //     setClient(null); // Directly update the `client` state
  //   }
  // };

  // const handleSearchButtonClick = async () => {
  //   if (!jobOrder.client.id) {
  //     console.log("No client selected");
  //     return;
  //   }

  //   if (uiState.isFetchingClient) {
  //     console.log("Already fetching client details...");
  //     return;
  //   }

  //   setIsLoading(true);
  //   setUIState((prev) => ({
  //     ...prev,
  //     isFetchingClient: true,
  //   }));

  //   try {
  //     const fetchedClient = await getClient(jobOrder.client.id);
  //     setClient(fetchedClient);

  //     // Logic to prepopulate jobOrder.client
  //     const updatedClient = { ...jobOrder.client };

  //     // Assign address if only one exists
  //     if (fetchedClient.addresses?.length === 1) {
  //       updatedClient.address = fetchedClient.addresses[0];
  //     } else {
  //       updatedClient.address = {};
  //     }

  //     if (fetchedClient.contacts?.length === 1) {
  //       const singleContact = fetchedClient.contacts[0];
  //       updatedClient.contact = {
  //         ...singleContact,
  //         number:
  //           singleContact.numbers?.length === 1 ? singleContact.numbers[0] : "",
  //         email:
  //           singleContact.emails?.length === 1 ? singleContact.emails[0] : "",
  //       };
  //     } else {
  //       updatedClient.contact = {};
  //     }

  //     // Update jobOrder with the updated client details
  //     setJobOrder((prev) => ({
  //       ...prev,
  //       client: updatedClient,
  //     }));
  //   } catch (error) {
  //     console.error("Error fetching client details:", error);
  //   } finally {
  //     setIsLoading(false);

  //     setUIState((prev) => ({
  //       ...prev,

  //       isFetchingClient: false,
  //     }));
  //   }
  // };

  const handleJobTypesChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedJobTypes = isChecked
        ? [...prev.job_types, formattedOption] // Add formattedOption if checked
        : prev.job_types.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedJobTypes === prev.job_types) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        job_types: updatedJobTypes,
      };
    });
  };

  const handleJobDescriptionChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedJobDescription = isChecked
        ? [...prev.job_description, formattedOption] // Add formattedOption if checked
        : prev.job_description.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedJobDescription === prev.job_description) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        job_description: updatedJobDescription,
      };
    });
  };

  const handleEndorserNameChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handleEndorserContactNumberChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handleCurrentStatusChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the field based on the `field` variable
    }));
  };

  const handlePaymentMethodChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the payment_method field
    }));
  };

  const handleDocumentsToBringChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedDocumentsToBring = isChecked
        ? [...prev.documents_to_bring, formattedOption] // Add formattedOption if checked
        : prev.documents_to_bring.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedDocumentsToBring === prev.documents_to_bring) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        documents_to_bring: updatedDocumentsToBring,
      };
    });
  };


  const handleEquipmentChange = (e, formattedOption) => {
    setJobOrder((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedEquipment = isChecked
        ? [...prev.equipment, formattedOption] // Add formattedOption if checked
        : prev.equipment.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedEquipment === prev.equipment) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        equipment: updatedEquipment,
      };
    });
  };

  const handleFormTypeChange = (e) => {
    setJobOrder((prev) => ({
      ...prev,
      form_type: e.target.value,
    }));
  };

  const handleScheduleConfirmedChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the payment_method field
    }));
  };

  const handleClientComplaintsChange = (field, value) => {
    setJobOrder((prev) => ({
      ...prev, // Preserve the rest of the jobOrder
      [field]: value, // Dynamically update the payment_method field
    }));
  };

  const handleTechCheckboxChange = (techId) => (event) => {
    // Prevent the event from bubbling up and triggering the dialog's onClose
    event.stopPropagation();
    if (event.target.checked) {
      setSelectedTechIds((prev) => [...prev, techId]);
    } else {
      setSelectedTechIds((prev) => prev.filter((id) => id !== techId));
    }
  };

  const handleTechAssign = () => {
    // First, clear technicians from the job order
    setJobOrder((prevJobOrder) => ({
      ...prevJobOrder,
      technicians: [],
    }));


    setJobOrder((prevJobOrder) => ({
      ...prevJobOrder,
      technicians: technicians
        .filter((tech) => selectedTechIds.includes(tech.id))
        .map(({ id, firstname, lastname }) => ({ id, firstname, lastname })),
    }));

    handleCloseAssignment();
  };

  // job order form validation
  const [errors, setErrors] = useState([]);

  // whenever errors show up, clear them after 5s
  useEffect(() => {
    if (errors.length === 0) return;
    const timer = setTimeout(() => setErrors([]), 7000);
    return () => clearTimeout(timer);
  }, [errors]);


  async function handleSubmit(e) {
    e.preventDefault();

    const storedUser = localStorage.getItem("user");
    const user_update = storedUser ? JSON.parse(storedUser).username : "";

    // Validate required fields
    // use Yup helper
    const validationErrors = await validateJobOrder(jobOrder);
    if (validationErrors.length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setErrorMessage("");
      setIsLoading(true);

      // 1) First, call the logging Lambda with the NEW jobOrder data,
      //    while DynamoDB still holds the OLD values. The Lambda will do a GetItem
      //    on boaz-job-orders (old values) and compare versus this payload.
      await postJobOrderLogs(id, user_update, jobOrder);

      // 2) Now that the “diff” is recorded, write the updated jobOrder back into DynamoDB:
      await putJobOrder(id, {
        ...jobOrder,
        user_update: user_update
      });

      setSuccessMessage("Job Order updated successfully!");
    } catch (error) {
      setErrorMessage(
        `An error occurred while updating the Job Order: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <BaseFormWrapper
      titleIcon={<WorkIcon />}
      title="Operations"
      subTitle="Manage Job Orders"
      subTitleRoute="/manage-job-orders"
      formTitle="Edit Job Order"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit} className="job-order-form">        
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Client Information
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Job Order ID row */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Job Order ID"
                      value={id || ""}
                      size="small"
                      fullWidth
                      disabled
                    />
                  </Grid>

                  {/* Client Name + Client ID + Work Permit chip */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Client Name"
                          variant="outlined"
                          fullWidth
                          value={jobOrder?.client?.name || ""}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Client ID"
                          variant="outlined"
                          fullWidth
                          value={jobOrder?.client?.id || ""}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md="auto" sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                        {client?.work_permit_required ? (
                          <Chip
                            icon={<BadgeIcon sx={{ fontSize: 18 }} />}
                            label="WORK PERMIT REQUIRED"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "rgba(25, 118, 210, 0.6)",
                              color: "#1976d2",
                              bgcolor: "rgba(25, 118, 210, 0.08)",
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                              height: 30,
                            }}
                          />
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={10}>
                        <FormControl
                          fullWidth
                          disabled={isLoading || !client}
                        >
                          <InputLabel id="job-site-address-label">
                            Job Site Address
                          </InputLabel>
                          <Select
                            labelId="job-site-address"
                            value={jobOrder.client.address?.id || ""}
                            onChange={(e) => {
                              const selectedAddress =
                                client?.addresses?.find(
                                  (address) =>
                                    address.id === e.target.value
                                );
                              handleAddressChange(selectedAddress); // Pass the full address object
                            }}
                            label="Job Site Address"
                          >
                            {(client?.addresses || []).map(
                              ({
                                id,
                                house_unit_street,
                                barangay,
                                city,
                                metro_province,
                                zipcode,
                              }) => (
                                <MenuItem key={id} value={id}>
                                  {`${house_unit_street || ""}, ${barangay || ""}, ${city || ""}, ${metro_province || ""} ${zipcode || ""}`}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder.client.address?.area || ""}
                          fullWidth
                          disabled={
                            isLoading || !jobOrder.client.address?.id
                          } // Disable if no address is selected
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      disabled={isLoading || !client}
                    >
                      <InputLabel id="contact-person-label">
                        Contact Person
                      </InputLabel>
                      <Select
                        labelId="contact-person-label"
                        value={jobOrder.client.contact?.id || ""}
                        onChange={(e) => {
                          const selectedContact =
                            client?.contacts?.find(
                              (contact) => contact.id === e.target.value
                            );
                          handleContactChange(selectedContact); // Pass the full contact object
                        }}
                        label="Contact Person"
                      >
                        {(client?.contacts || []).map(
                          ({ id, firstname, lastname }) => (
                            <MenuItem key={id} value={id}>
                              {`${lastname}, ${firstname}`}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      disabled={
                        isLoading || !client || !jobOrder.client.contact
                      }
                    >
                      <InputLabel id="contact-email-label">
                        Contact Email
                      </InputLabel>
                      <Select
                        labelId="contact-email-label"
                        value={jobOrder.client.contact?.email?.id || ""}
                        onChange={(e) => {
                          const selectedEmail =
                            jobOrder.client.contact.emails.find(
                              (email) => email.id === e.target.value
                            );
                          handleContactEmailChange(selectedEmail); // Pass the full email object
                        }}
                        label="Contact Email"
                      >
                        {(jobOrder.client.contact?.emails || []).map(
                          (email) => (
                            <MenuItem key={email.id} value={email.id}>
                              {email.email}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      disabled={
                        isLoading || !client || !jobOrder.client.contact
                      }
                    >
                      <InputLabel id="contact-number-label">
                        Contact Number
                      </InputLabel>
                      <Select
                        labelId="contact-number-label"
                        value={
                          jobOrder.client.contact?.number?.id || ""
                        }
                        onChange={(e) => {
                          const selectedNumber =
                            jobOrder.client.contact.numbers.find(
                              (number) => number.id === e.target.value
                            );
                          handleContactNumberChange(selectedNumber); // Pass the full number object
                        }}
                        label="Contact Number"
                      >
                        {(jobOrder.client.contact?.numbers || []).map(
                          (number) => (
                            <MenuItem key={number.id} value={number.id}>
                              {number.type}: {number.number}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Category"
                      variant="outlined"
                      value={client?.category || ''}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Parent Client"
                      variant="outlined"
                      value={client?.parent_client?.name || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Job Type
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...JOB_TYPES]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.job_types || []).includes(formattedOption)}
                            onChange={(e) => handleJobTypesChange(e, formattedOption)}
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>

            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Job Description
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...JOB_DESCRIPTIONS]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.job_description || []).includes(formattedOption)}
                            onChange={(e) => handleJobDescriptionChange(e, formattedOption)}
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>

            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder.special_notes || ""}
                      onChange={(e) =>
                        handleJobOrderSpecialNotesChange(
                          "special_notes",
                          e.target.value
                        )
                      }
                      fullWidth
                      disabled={isLoading}
                      multiline
                      rows={4}
                    />
                  </Grid>

                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Schedule Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Schedule
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={2}>
                    <TextField
                      label="Date"
                      name="job_date"
                      type="date"
                      value={jobOrder.job_date || ""}
                      onChange={(e) =>
                        handleJobOrderScheduleChange(
                          "job_date",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Start Time"
                      name="start_time"
                      type="time"
                      value={jobOrder.start_time || ""}
                      onChange={(e) =>
                        handleJobOrderScheduleChange(
                          "start_time",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="End Time"
                      name="end_time"
                      type="time"
                      value={jobOrder.end_time}
                      onChange={(e) =>
                        handleJobOrderScheduleChange(
                          "end_time",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>                  
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="h6">Appointment</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Appointment Type */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        value={jobOrder?.appointment_type || ""}
                        label="Appointment Type"
                        onChange={e =>
                          setJobOrder(prev => ({
                            ...prev,
                            appointment_type: e.target.value
                          }))
                        }
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Treatment Type */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        value={jobOrder?.treatment_type || ""}
                        label="Treatment Type"
                        onChange={e =>
                          setJobOrder(prev => ({
                            ...prev,
                            treatment_type: e.target.value
                          }))
                        }
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Endorsement Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Endorsement Form
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent
              >
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Name"
                      name="endorser_name"
                      type="text"
                      value={jobOrder.endorser_name || ""}
                      onChange={(e) =>
                        handleEndorserNameChange(
                          "endorser_name",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Contact Number"
                      name="endorser_contact_number"
                      type="text"
                      value={jobOrder.endorser_contact_number || ""}
                      onChange={(e) =>
                        handleEndorserContactNumberChange(
                          "endorser_contact_number",
                          e.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Current Status"
                      name="current_status"
                      value={jobOrder.current_status || ""}
                      onChange={(e) =>
                        handleCurrentStatusChange("current_status", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="heavily_infested">Heavily Infested</MenuItem>
                      <MenuItem value="medium_infestation">Medium Infestation</MenuItem>
                      <MenuItem value="light_infestation">Light Infestation</MenuItem>
                      <MenuItem value="for_inspection_first_before_treament">
                        For Inspection First Before Treament
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Payment Method"
                      name="payment_method"
                      value={jobOrder.payment_method || ""}
                      onChange={(e) =>
                        handlePaymentMethodChange("payment_method", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="COD">COD</MenuItem>
                      <MenuItem value="For Billing">For Billing</MenuItem>
                      <MenuItem value="Check collection">Check collection</MenuItem>
                      <MenuItem value="Bank deposit">Bank deposit</MenuItem>
                      <MenuItem value="Online/Gcash">Online/Gcash</MenuItem>
                      <MenuItem value="Paymongo">Paymongo</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Schedule Confirmed with Clients"
                      name="schedule_confirmed"
                      value={jobOrder.schedule_confirmed || ""}
                      onChange={(e) =>
                        handleScheduleConfirmedChange("schedule_confirmed", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      select
                      label="With Client Complaints"
                      name="client_complaints"
                      value={jobOrder.client_complaints || ""}
                      onChange={(e) =>
                        handleClientComplaintsChange("client_complaints", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled={isLoading}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Required Documents Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Required Documents to Bring
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...DOCUMENTS_TO_BRING]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.documents_to_bring || []).includes(formattedOption)}
                            onChange={(e) => handleDocumentsToBringChange(e, formattedOption)}
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          {/* Equipment Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Equipment to Bring
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...EQUIPMENT]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.equipment || []).includes(formattedOption)}
                            onChange={(e) => handleEquipmentChange(e, formattedOption)}
                          />
                        }
                        label={option}
                        disabled={isLoading}
                      />
                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          {/* Technicians Section */}
          <Grid item xs={6} md={6}>
            <Card
              variant="outlined"
              sx={{ marginRight: "5px" }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Technicians
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Button variant="outlined" color="primary" onClick={handleOpenModal}>
                      Assign Technicians
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    {(() => {
                      // Convert the startDate to a weekday name (e.g., Monday, Tuesday, etc.)
                      const startDateObj = new Date(jobOrder.job_date);
                      const options = { weekday: "long" };
                      const startDayName = startDateObj.toLocaleDateString("en-US", options);

                      return (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Array.isArray(jobOrder.technicians) && jobOrder.technicians.length > 0 ? (
                              jobOrder.technicians.map((tech) => {
                                // Find the full technician details from the global "technicians" array using the technician id
                                const fullTech = technicians.find((t) => t.id === tech.id);
                                // Compute the basic notes from competency levels
                                const basicNotes = [];
                                if (
                                  fullTech &&
                                  fullTech.competency_level &&
                                  fullTech.competency_level.driving_4_wheels &&
                                  fullTech.competency_level.driving_4_wheels !== "N/A"
                                ) {
                                  basicNotes.push("Driver: 4 wheels");
                                }
                                if (
                                  fullTech &&
                                  fullTech.competency_level &&
                                  fullTech.competency_level.driving_2_wheels &&
                                  fullTech.competency_level.driving_2_wheels !== "N/A"
                                ) {
                                  basicNotes.push("Driver: 2 wheels");
                                }

                                return (
                                  <TableRow key={tech.id}>

                                    <TableCell>{`${tech.lastname}, ${tech.firstname}`}</TableCell>
                                    <TableCell>
                                      {basicNotes.join(", ")}
                                      {fullTech && fullTech.rest_day && fullTech.rest_day.includes(startDayName) && (
                                        <span style={{ display: "block", color: "red" }}>Restday</span>
                                      )}
                                      {jobOrder?.job_date ? (
                                        fullTech &&
                                        (fullTech.off_service || [])
                                          .filter((off) => off.date === jobOrder.job_date)
                                          .map((off, index) => (
                                            <span key={index} style={{ display: "block", color: "red" }}>
                                              {off.reason.charAt(0).toUpperCase() + off.reason.slice(1)}
                                            </span>
                                          ))
                                      ) : (
                                        <div style={{ display: "block", color: "red" }}>
                                          No Job Order Date. Can't check rest day.
                                        </div>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3}>No technicians available</TableCell>
                              </TableRow>
                            )}

                          </TableBody>
                        </Table>
                      );
                    })()}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Dialog open={modalOpen} onClose={handleDialogClose}>
            <DialogTitle
              sx={{
                fontSize: "1.25rem",
                fontWeight: "bold",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
                mb: 2,
              }}
            >
              Assign Technicians
            </DialogTitle>
            <DialogContent>
              {(() => {
                // Convert the startDate to a weekday name (e.g., Monday, Tuesday, etc.)
                const startDateObj = new Date(jobOrder.job_date);
                const options = { weekday: "long" };
                const startDayName = startDateObj.toLocaleDateString("en-US", options);

                return (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>Name</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(technicians) && technicians.length > 0 ? (
                        technicians.map((tech) => {
                          const notes = [];
                          if (
                            tech.competency_level.driving_4_wheels &&
                            tech.competency_level.driving_4_wheels !== "N/A"
                          ) {
                            notes.push("Driver: 4 wheels");
                          }
                          if (
                            tech.competency_level.driving_2_wheels &&
                            tech.competency_level.driving_2_wheels !== "N/A"
                          ) {
                            notes.push("Driver: 2 wheels");
                          }
                          return (
                            <TableRow key={tech.id}>
                              <TableCell>
                                <Checkbox
                                  size="small"
                                  checked={selectedTechIds.includes(tech.id)}
                                  // Stop propagation so that clicking the checkbox doesn't trigger the dialog's onClose
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={handleTechCheckboxChange(tech.id)}
                                />
                              </TableCell>
                              <TableCell>{`${tech.lastname}, ${tech.firstname}`}</TableCell>
                              <TableCell>
                                {notes.join(", ")}
                                {tech.rest_day && tech.rest_day.includes(startDayName) && (
                                  <span style={{ display: "block", color: "red" }}>Restday</span>
                                )}
                                {jobOrder?.job_date ? (
                                  // Ensure tech.off_service is an array before mapping
                                  (tech.off_service || [])
                                    .filter((off) => off.date === jobOrder.job_date)
                                    .map((off, index) => (
                                      <span key={index} style={{ display: "block", color: "red" }}>
                                        {off.reason.charAt(0).toUpperCase() + off.reason.slice(1)}
                                      </span>
                                    ))
                                ) : (
                                  <div style={{ display: "block", color: "red" }}>No Job Order Date. Can't check rest day.</div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3}>No technicians available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleTechAssign} sx={{ fontSize: "1rem" }} variant="contained">
                Assign
              </Button>
              <Button onClick={handleCloseAssignment} sx={{ fontSize: "1rem" }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Form Type Section */}
          <Grid item xs={6} md={6}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="h6">Form Type</Typography>}
                sx={{ padding: 0 }}
              />
              <CardContent>
                <TextField
                  select
                  label="Form Type"
                  name="form_type"
                  value={jobOrder.form_type || ""}
                  onChange={handleFormTypeChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled={isLoading}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="makati_gpc_form">Makati GPC Form</MenuItem>
                  <MenuItem value="qc_form">QC Form</MenuItem>
                  <MenuItem value="termite_control_form">Termite Control Form</MenuItem>
                  <MenuItem value="transmittal_form">Transmittal Form</MenuItem>
                </TextField>
              </CardContent>
            </Card>
          </Grid>

          {/* Submit and Cancel Buttons Section */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-job-orders")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Oops—please fix these:</AlertTitle>
            <List dense disablePadding>
              {errors.map((msg, i) => (
                <ListItem key={i} sx={{ display: 'list-item', pl: 2 }}>
                  {msg}
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </form>
    </BaseFormWrapper>
  );
}

export default EditJobOrder;
